import { bandScore } from "@/lib/score";
import { AlertTriangle, CheckCircle2, Circle, Loader2, FlaskConical, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { hazardTag } from "@/lib/hazard";
import { ExposureBlock } from "@/components/ExposureBlock";
import { ImpactBadge } from "@/components/ImpactBadge";
import { TimelineInline } from "@/components/TimelineBlock";

export const FORECAST_META = {
  escalating: { color: "text-atlas-orange", ring: "border-atlas-orange/40 bg-atlas-orange/5", Icon: TrendingUp },
  watch: { color: "text-amber-400", ring: "border-amber-400/40 bg-amber-400/5", Icon: TrendingUp },
  easing: { color: "text-emerald-400", ring: "border-emerald-400/40 bg-emerald-400/5", Icon: TrendingDown },
  stable: { color: "text-atlas-secondary", ring: "border-white/10 bg-white/[0.02]", Icon: Minus },
  na: null,
};

const STATUS_META = {
  unexplained_anomaly: {
    label: "UNEXPLAINED ANOMALY",
    color: "text-atlas-red",
    dot: "bg-atlas-red",
    border: "border-l-atlas-red",
    Icon: AlertTriangle,
  },
  explained_anomaly: {
    label: "EXPLAINED ANOMALY",
    color: "text-atlas-orange",
    dot: "bg-atlas-orange",
    border: "border-l-atlas-orange",
    Icon: CheckCircle2,
  },
  nominal: {
    label: "NOMINAL",
    color: "text-atlas-cyan",
    dot: "bg-atlas-cyan",
    border: "border-l-atlas-cyan/40",
    Icon: Circle,
  },
};

const Field = ({ k, v }) => (
  <div className="flex justify-between gap-2 py-0.5 min-w-0">
    <span className="text-[10px] uppercase tracking-wider text-atlas-secondary/85 font-mono shrink-0">{k}</span>
    <span className="text-[11px] text-atlas-foreground font-mono text-right truncate min-w-0" title={v}>
      {v}
    </span>
  </div>
);

const urgencyTone = (u) => (u >= 75 ? "bg-atlas-red text-white" : u >= 55 ? "bg-atlas-orange text-atlas-void" : "bg-white/15 text-white");

export const ObservationCard = ({ obs, topHyp, onSelect, active, rank, urgency, ahead, extra }) => {
  const meta = STATUS_META[obs.triage_status] || STATUS_META.nominal;
  const p = obs.provenance || {};
  const analyzing = obs.triage_status === "unexplained_anomaly" && !obs.has_hypothesis;

  return (
    <div
      onClick={() => onSelect(obs)}
      data-testid={`observation-card-${obs.uid}`}
      className={`ledger-row cursor-pointer border-b border-l-2 ${meta.border} border-white/10 p-3 ${
        active ? "bg-white/[0.04]" : ""
      } ${obs.triage_status === "unexplained_anomaly" ? "alert-glow" : ""}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {rank != null && (
            <span className={`text-[10px] font-mono font-bold px-1.5 ${urgencyTone(urgency)}`} data-testid={`incident-urgency-single-${obs.uid}`}
              title="Urgency 0–100: severity past threshold, people exposed, growth, breadth, and whether an agency has registered an event yet">
              #{rank} · {urgency}
            </span>
          )}
          {ahead && <span className="text-[9px] font-mono uppercase tracking-widest text-atlas-cyan" title="No agency has registered an event here yet. This is not a claim that Vigilo Earth spotted it first — lead time is only claimed after an agency confirms.">No agency alert yet ·</span>}
          <meta.Icon className={`w-3 h-3 ${meta.color}`} strokeWidth={2} />
          <span className={`text-[9px] font-mono tracking-widest ${meta.color}`}>{meta.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <ImpactBadge impact={obs.impact} testId={`impact-badge-${obs.uid}`} />
          <span className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 border border-white/15 text-atlas-secondary/80">
            {hazardTag(obs)}
          </span>
          <span className="text-[10px] font-mono text-atlas-secondary/80">{obs.uid.slice(0, 8)}</span>
        </div>
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-display text-white truncate pr-2">{obs.region}</span>
        <span className={`text-base font-mono ${meta.color}`}>
          {obs.deviation_sigma >= 0 ? "+" : ""}
          {obs.deviation_sigma}σ
        </span>
      </div>
      {extra}

      <div className="border-t border-white/5 pt-1.5 grid grid-cols-2 gap-x-6">
        <Field k="Source" v={`${p.source} · ${p.instrument}`} />
        <Field k="Value" v={`${obs.value} (base ${obs.seasonal_baseline_mean})`} />
        <Field k="Resolution" v={p.spatial_resolution} />
        <Field k="Method" v={p.measurement_method} />
        <Field k="Data observed" v={(p.observed_at || "").replace("T", " ").slice(0, 16) + "Z"} />
        <Field k="First flagged by Vigilo Earth" v={obs.first_detected_at ? obs.first_detected_at.replace("T", " ").slice(0, 16) + "Z" : "—"} />
        <Field k="Coordinates" v={`${obs.lat}, ${obs.lon}`} />
      </div>
      {obs.hazard === "seismic" && (
        <p className="text-[9px] font-mono text-atlas-secondary/85 mt-1" data-testid={`seismic-note-${obs.uid}`}>
          Built from public USGS readings — lead time counts only if a later M5+ quake is confirmed.
        </p>
      )}

      {obs.forecast && FORECAST_META[obs.forecast.outlook] && (
        (() => {
          const fm = FORECAST_META[obs.forecast.outlook];
          return (
            <div
              data-testid={`forecast-line-${obs.uid}`}
              className={`mt-2 flex items-start gap-1.5 border ${fm.ring} px-2 py-1.5`}
            >
              <fm.Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${fm.color}`} strokeWidth={2} />
              <div className="min-w-0">
                <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/80">
                  72-hour outlook
                </div>
                <div className={`text-[11px] leading-snug ${fm.color}`}>{obs.forecast.headline}</div>
              </div>
            </div>
          );
        })()
      )}

      {obs.exposure && (
        <ExposureBlock exposure={obs.exposure} variant="compact" testId={`exposure-${obs.uid}`} />
      )}

      <TimelineInline obs={obs} testId={`timeline-inline-${obs.uid}`} />

      {topHyp && (
        <div
          data-testid={`observation-atlas-read-${obs.uid}`}
          className="mt-2 border-l-2 border-atlas-cyan/50 bg-atlas-cyan/5 pl-2.5 py-1.5 pr-2"
        >
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[8px] font-mono uppercase tracking-widest text-atlas-cyan/70 flex items-center gap-1">
              <FlaskConical className="w-3 h-3" /> Vigilo Earth read
            </span>
            <span className="text-[9px] font-mono text-atlas-cyan">
              {`${bandScore(topHyp).src} ${Math.round(bandScore(topHyp).v * 100)}%`}
            </span>
          </div>
          <div className="text-[11px] text-white/90 leading-snug">{topHyp.plain_summary || topHyp.name}</div>
          <div className="text-[9px] font-mono text-atlas-secondary/75 mt-0.5">Click for full hypotheses →</div>
        </div>
      )}

      {analyzing && !topHyp && (
        <div
          data-testid={`observation-analyzing-${obs.uid}`}
          className="mt-2 flex items-center gap-1.5 text-[10px] font-mono text-atlas-secondary/80"
        >
          <Loader2 className="w-3 h-3 animate-spin text-atlas-cyan" /> Vigilo Earth is analyzing this anomaly…
        </div>
      )}
      {obs.has_hypothesis && !topHyp && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] font-mono text-atlas-cyan/80">
          <FlaskConical className="w-3 h-3" /> Hypotheses recorded →
        </div>
      )}
    </div>
  );
};
